import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, CheckCircle, PencilSimpleLine, FileText } from "@phosphor-icons/react";
import { C } from "@/components/fm/shared";
import { David, ScenarioCards, DscrVerdict, CrsFinal } from "@/components/fm/FmFinal";
import { buildQuestions, normalizeModelCtx, fyLabels, SECTION_NAMES } from "@/lib/fmInterview";

/* ── path helpers ──────────────────────────────────────────────── */
const dget = (obj, path) => path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
const dset = (obj, path, value) => {
  const keys = path.split(".");
  const clone = { ...obj };
  let cur = clone;
  keys.slice(0, -1).forEach((k) => { cur[k] = { ...(cur[k] || {}) }; cur = cur[k]; });
  cur[keys[keys.length - 1]] = value;
  return clone;
};

const inputCls = "w-full px-3 py-3 rounded-lg text-base text-white placeholder:text-white/25 focus:outline-none focus:border-[#0A8B85]";
const inputStyle = { backgroundColor: "rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.15)" };


const fmtAnswer = (q, ctx) => {
  if (q.type === "info" || q.type === "unitecon" || q.type === "dscr") return "✓";
  const v = q.path ? dget(ctx, q.path) : undefined;
  if (q.type === "rates5") {
    const rr = v || q.defaults || {};
    const labels = fyLabels(ctx);
    return ["f1", "f2", "f3", "f4", "f5"].map((y, i) => `${labels[i]} ${Math.round((rr[y] ?? 0) * 100)}%`).join(" · ");
  }
  if (q.type === "scenarios3") {
    const s = v || q.defaults;
    const f = (x) => `${Math.round((x ?? 0) * 100)}%`;
    const dn = s.downside || {}, up = s.upside || {};
    return `down ${f(dn.volume_delta)}/${f(dn.price_delta)}/${f(dn.cogs_delta)} · up ${f(up.volume_delta)}/${f(up.price_delta)}/${f(up.cogs_delta)}`;
  }
  if (q.type === "list") return `${(v || []).length} ${q.itemLabel}${(v || []).length === 1 ? "" : "s"}`;
  if (q.type === "hist3") {
    const h = v || {};
    return ["h3", "h2", "h1"].map((k) => (h[k]?.revenue ? Number(h[k].revenue).toLocaleString("en-KE") : "—")).join(" · ");
  }
  if (v == null || v === "") return "—";
  if (q.type === "percent") return `${Math.round(Number(v) * 10000) / 100}%`;
  if (q.type === "select") return (q.options.find((o) => o.v === v) || {}).label || String(v);
  if (q.type === "number") return `${Number(v).toLocaleString("en-KE")}${q.suffix ? " " + q.suffix : ""}`;
  return String(v);
};

export default function FmInterview({ model, setModel, mode, onCreateModel, startSection = 1, onDone }) {
  const navigate = useNavigate();
  const [section, setSection] = useState(startSection);
  const [qIdx, setQIdx] = useState(0);
  const [phase, setPhase] = useState("question"); // question | summary | final
  const [draft, setDraft] = useState(null);       // null = not initialised for current q
  const [rows, setRows] = useState(null);         // list-type working rows
  const [saving, setSaving] = useState(false);
  const [crs, setCrs] = useState(null);
  const [ue, setUe] = useState(null);
  const [dscrData, setDscrData] = useState(null);
  const [scn, setScn] = useState(null);
  const [bkeven, setBkeven] = useState(null);
  const [returnTo, setReturnTo] = useState(null);
  const [opexWarn, setOpexWarn] = useState(false);
  const [opexAcked, setOpexAcked] = useState(false);

  const ctx = normalizeModelCtx(model);
  const YL = fyLabels(ctx);
  const questions = useMemo(() => buildQuestions(ctx), [ctx]);
  const sectionQs = questions.filter((x) => x.section === section);
  const q = sectionQs[qIdx];
  const imported = model?.import_session?.status === "applied";
  const importFiles = (model?.import_session?.files || []).join(", ");

  /* current stored value → editor state */
  const stored = q && q.path ? dget(ctx, q.path) : undefined;
  const prefilled = imported && q && q.path && !q.path.startsWith("_local") && stored != null && stored !== "" &&
    !(Array.isArray(stored) && !stored.length) && q.type !== "info" && q.type !== "scenario";

  const initDraft = () => {
    if (!q) return "";
    if (q.type === "rates5") return { ...q.defaults, ...(stored || {}) };
    if (q.type === "scenarios3") {
      const s = stored || {};
      return {
        downside: { ...q.defaults.downside, ...(s.downside || {}) },
        upside: { ...q.defaults.upside, ...(s.upside || {}) },
      };
    }
    if (q.type === "hist3") {
      const h = stored || {};
      return { h3: h.h3?.revenue ?? "", h2: h.h2?.revenue ?? "", h1: h.h1?.revenue ?? "" };
    }
    if (q.type === "percent") return stored != null && stored !== "" ? Math.round(Number(stored) * 10000) / 100 : "";
    return stored ?? "";
  };
  const initRows = () => {
    if (!q || q.type !== "list") return [];
    if (q.fromModel) return q.fromModel(stored);
    return (stored || []).map((r) => ({ ...r }));
  };
  const d = draft === null ? initDraft() : draft;
  const listRows = rows === null ? initRows() : rows;

  const persist = async (nextModel, path) => {
    if (mode !== "live" || !path || path.startsWith("_local")) return;
    const top = path.split(".")[0];
    let patch;
    if (top === "assumptions") {
      const sec = path.split(".")[1];
      patch = { assumptions: { [sec]: nextModel.assumptions[sec] } };
    } else if (top === "profile") {
      patch = { profile: nextModel.profile };
    } else if (top === "milestones") {
      patch = { milestones: nextModel.milestones };
    } else if (top === "historicals") {
      patch = { historicals: nextModel.historicals };
    } else return;
    setSaving(true);
    try {
      await api.put(`/fm/models/${nextModel.fm_id}`, patch);
    } catch {
      toast.error("Couldn't save that answer — check your connection");
    }
    setSaving(false);
  };

  const commit = async (value) => {
    let next = ctx;
    const passive = q.type === "info" || q.type === "unitecon" || q.type === "dscr";
    if (!passive) next = dset(ctx, q.path, value);
    const touched = new Set([q.path]);
    if (q.alsoSet) {
      for (const [p, v] of q.alsoSet(next, value) || []) {
        next = dset(next, p, v);
        touched.add(p);
      }
    }
    setModel(next);
    if (!passive) {
      const tops = [...new Set([...touched].map((p) => p.split(".").slice(0, 2).join(".")))];
      for (const t of tops) await persist(next, t);
    }
    advance(next);
  };

  const advance = (next) => {
    setDraft(null); setRows(null);
    const qs = buildQuestions(next).filter((x) => x.section === section);
    if (qIdx + 1 < qs.length) setQIdx(qIdx + 1);
    else if (section === 8) { setSection(9); setQIdx(0); } // Prompt 4: no summary — straight to DSCR
    else if (section >= 9) goFinal();
    else setPhase("summary");
  };

  const submitCurrent = () => {
    if (q.type === "info" || q.type === "unitecon" || q.type === "dscr") return commit(undefined);
    if (q.type === "list") {
      const clean = listRows.filter((r) => Object.values(r).some((v) => v !== "" && v != null));
      return commit(q.transform ? q.transform(clean) : clean);
    }
    if (q.type === "scenarios3") return commit({ downside: { ...d.downside }, upside: { ...d.upside } });
    if (q.type === "rates5") return commit({ ...d });
    if (q.type === "hist3") {
      return commit({
        h3: { revenue: Number(d.h3) || 0 },
        h2: { revenue: Number(d.h2) || 0 },
        h1: { revenue: Number(d.h1) || 0 },
      });
    }
    if (q.type === "percent") {
      if (d === "" || d == null) return toast.error("Enter a number, or use the typical default");
      return commit(Number(d) / 100);
    }
    if (q.type === "number") {
      if (d === "" || d == null) return toast.error("Enter a number, or use the typical default");
      return commit(Number(d));
    }
    if (q.type === "select") {
      if (!d) return toast.error("Pick one option");
      return commit(d);
    }
    if (!String(d || "").trim()) return toast.error("This one needs an answer");
    return commit(d);
  };

  const useDefault = () => {
    if (q.default === undefined) return;
    commit(q.default);
  };

  /* Live compute fetch for the Section 7 unit-economics, Section 8 chart and Section 9 DSCR screens */
  useEffect(() => {
    if ((q?.type === "unitecon" || q?.type === "dscr" || q?.type === "scenarios3") && mode === "live" && model?.fm_id) {
      setUe(null); setDscrData(null); setScn(null);
      api.get(`/fm/models/${model.fm_id}/compute`)
        .then((r) => { setUe(r.data?.unit_economics || null); setDscrData(r.data?.dscr || null); setScn(r.data?.scenarios || null); })
        .catch(() => { setUe(null); setDscrData(null); setScn(null); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q?.id, mode, model?.fm_id]);

  const goFinal = async () => {
    setPhase("final"); setSection(10); setReturnTo(null); setCrs(null); setBkeven(null);
    try {
      const { data } = await api.get(`/fm/models/${model.fm_id}/compute`);
      setCrs(data?.crs || null);
      if (data?.dashboard) setBkeven({ month: data.dashboard.breakeven_month, label: data.dashboard.breakeven_label });
    } catch { setCrs(null); }
  };

  const confirmSection = async () => {
    if (section === 1 && mode === "local" && onCreateModel && !ctx.fm_id && !saving) {
      setSaving(true);
      try {
        await onCreateModel(ctx);
      } catch (e) {
        setSaving(false);
        return toast.error(e?.response?.data?.detail || "Could not create your model");
      }
      setSaving(false);
      toast.success("Model created — your answers now save live");
    }
    // Prompt-3 rule: warn when Year-1 OpEx exceeds gross profit (EBITDA < 0)
    if (section === 4 && mode === "live" && model?.fm_id && !opexAcked) {
      setOpexAcked(true);
      try {
        const { data } = await api.get(`/fm/models/${model.fm_id}/compute`);
        const e1 = data?.dashboard?.series?.annual_ebitda?.[0];
        if (e1 != null && e1 < 0) { setOpexWarn(true); return; }
      } catch { /* non-blocking */ }
    }
    setOpexWarn(false);
    if (returnTo === "crs" || section >= 9) return goFinal();
    setSection(section + 1); setQIdx(0); setPhase("question");
  };

  const jumpTo = (sec, idx = 0) => {
    if (sec >= 10) return goFinal();
    setSection(sec); setQIdx(idx); setPhase("question"); setDraft(null); setRows(null);
  };

  /* ── render ─────────────────────────────────────────────────── */
  return (
    <div data-testid="fm-interview">
      {/* Progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-[0.18em] mb-2">
          <span style={{ color: C.orange }} data-testid="fm-interview-progress">
            Section {Math.min(section, 10)} of 10 · {SECTION_NAMES[Math.min(section, 10) - 1]}
          </span>
          <span className="text-white/35 normal-case tracking-normal">{saving ? "Saving…" : mode === "live" ? "Saved live" : ""}</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(255,255,255,0.08)" }}>
          <div className="h-full rounded-full transition-all duration-300"
            style={{ width: `${(Math.min(section, 10) / 10) * 100}%`, backgroundColor: C.teal }} />
        </div>
        <div className="flex gap-1 mt-2 flex-wrap">
          {SECTION_NAMES.map((n, i) => (
            <button key={n} onClick={() => (mode === "live" || i === 0) && jumpTo(i + 1)}
              data-testid={`fm-section-chip-${i + 1}`}
              className="text-[9px] px-1.5 py-0.5 rounded font-semibold"
              style={{
                backgroundColor: section === i + 1 ? "rgba(10,139,133,0.25)" : "rgba(255,255,255,0.04)",
                color: section === i + 1 ? C.tealSoft : section > i + 1 ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.25)",
              }}>
              {i + 1}. {n}
            </button>
          ))}
        </div>
      </div>

      {/* FINAL — Section 10 · CRS (Prompt 4) */}
      {phase === "final" && (
        <CrsFinal crs={crs} model={model} breakeven={bkeven}
          onFix={(sec) => { setReturnTo("crs"); jumpTo(sec); }}
          onOpenModel={() => (onDone ? onDone() : navigate(`/fm/${model.fm_id}`))} />
      )}

      {/* SECTION SUMMARY */}
      {phase === "summary" && (
        <div className="space-y-6" data-testid="fm-section-summary">
          <David>
            <div className="text-base font-extrabold text-white">
              Section {section} — {SECTION_NAMES[section - 1]} — done. Does this look right before we move on?
            </div>
          </David>
          <div className="rounded-xl border overflow-hidden max-w-2xl" style={{ backgroundColor: C.panel, borderColor: "rgba(123,211,206,0.12)" }}>
            {sectionQs.map((sq, i) => (
              <div key={sq.id} className={`flex items-center gap-3 px-4 py-3 ${i > 0 ? "border-t border-white/5" : ""}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] text-white/55 truncate">{typeof sq.ask === "function" ? sq.ask(ctx) : sq.ask}</div>
                  <div className="text-sm font-bold text-white mt-0.5" data-testid={`fm-summary-${sq.id}`}>{fmtAnswer(sq, ctx)}</div>
                </div>
                {sq.type !== "info" && (
                  <button onClick={() => jumpTo(section, i)} data-testid={`fm-summary-edit-${sq.id}`}
                    className="text-[11px] font-bold inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-white/10"
                    style={{ color: C.tealSoft }}>
                    <PencilSimpleLine size={11} /> Edit
                  </button>
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-3">
            <button onClick={confirmSection} disabled={saving} data-testid="fm-summary-confirm"
              className="px-6 py-3 rounded-lg font-bold text-sm inline-flex items-center gap-2 disabled:opacity-60"
              style={{ backgroundColor: C.teal, color: "#fff" }}>
              <CheckCircle size={16} weight="fill" /> {saving ? "Setting up…" : "Yes, looks right"} <ArrowRight size={14} />
            </button>
          </div>
          {opexWarn && (
            <div className="rounded-xl p-5 border max-w-2xl" data-testid="fm-opex-warning"
              style={{ backgroundColor: "rgba(245,158,11,0.08)", borderColor: "rgba(245,158,11,0.4)" }}>
              <div className="text-sm font-bold" style={{ color: C.orange }}>Your costs exceed your gross profit in Year 1.</div>
              <div className="text-[13px] text-white/70 mt-1.5">
                This is common for early-stage businesses but funders will want to see when you break even. Want to continue?
              </div>
              <button onClick={confirmSection} data-testid="fm-opex-continue"
                className="mt-3 px-4 py-2 rounded-lg text-xs font-bold"
                style={{ backgroundColor: C.orange, color: "#000" }}>
                Yes, continue
              </button>
            </div>
          )}
        </div>
      )}

      {/* QUESTION */}
      {phase === "question" && q && (
        <div className="space-y-6" data-testid={`fm-q-${q.id}`}>
          <David>
            <div className="text-lg font-extrabold text-white leading-snug">{typeof q.ask === "function" ? q.ask(ctx) : q.ask}</div>
            {q.explain && <div className="text-[13px] text-white/60 mt-2 leading-relaxed">{q.explain}</div>}
          </David>

          {prefilled && (
            <div className="rounded-lg px-4 py-3 text-[12.5px] max-w-2xl flex items-start gap-2"
              style={{ backgroundColor: "rgba(10,139,133,0.1)", border: "1px solid rgba(10,139,133,0.35)", color: C.tealSoft }}
              data-testid="fm-prefill-banner">
              <FileText size={15} className="mt-0.5 shrink-0" />
              <span>I pre-filled this from your upload{importFiles ? ` (${importFiles})` : ""} — confirm it below, or correct it.</span>
            </div>
          )}

          <div className={`${q.type === "scenarios3" ? "max-w-4xl" : "max-w-2xl"} ml-12`}>
            {q.type === "info" && null}

            {(q.type === "text") && (
              <input type="text" className={inputCls} style={inputStyle} value={d}
                onChange={(e) => setDraft(e.target.value)} data-testid="fm-answer-input" autoFocus
                onKeyDown={(e) => e.key === "Enter" && submitCurrent()} />
            )}

            {(q.type === "number" || q.type === "percent") && (
              <div className="flex items-center gap-3">
                <input type="number" className={inputCls} style={inputStyle} value={d} step={q.step || "any"}
                  onChange={(e) => setDraft(e.target.value)} data-testid="fm-answer-input" autoFocus
                  onKeyDown={(e) => e.key === "Enter" && submitCurrent()} />
                <span className="text-sm text-white/50 shrink-0">{q.type === "percent" ? "%" : q.suffix || ""}</span>
              </div>
            )}

            {q.type === "select" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {q.options.map((o) => (
                  <button key={o.v} onClick={() => setDraft(o.v)} data-testid={`fm-opt-${String(o.v).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="text-left px-4 py-3 rounded-lg border-2 transition-all"
                    style={{
                      backgroundColor: d === o.v ? "rgba(10,139,133,0.14)" : "rgba(255,255,255,0.03)",
                      borderColor: d === o.v ? C.teal : "rgba(255,255,255,0.1)",
                    }}>
                    <div className="text-sm font-bold" style={{ color: d === o.v ? C.tealSoft : "#fff" }}>{o.label}</div>
                    {o.sub && <div className="text-[11px] text-white/45 mt-0.5">{o.sub}</div>}
                  </button>
                ))}
              </div>
            )}

            {q.type === "list" && (
              <div className="space-y-2">
                {listRows.map((r, i) => (
                  <div key={i} className="grid gap-2 items-end" style={{ gridTemplateColumns: `repeat(${q.fields.length}, 1fr) auto` }}>
                    {q.fields.map((f) => (
                      <div key={f.key}>
                        {i === 0 && <div className="text-[10px] uppercase tracking-wider text-white/40 font-semibold mb-1">{f.label}</div>}
                        <input type={f.type === "number" ? "number" : "text"}
                          className="w-full px-2.5 py-2 rounded-md text-sm text-white focus:outline-none" style={inputStyle}
                          value={r[f.key] ?? ""} data-testid={`fm-list-${q.id}-${i}-${f.key}`}
                          onChange={(e) => { const nr = listRows.map((x, xi) => xi === i ? { ...x, [f.key]: e.target.value } : x); setRows(nr); }} />
                      </div>
                    ))}
                    <button onClick={() => setRows(listRows.filter((_, xi) => xi !== i))} className="text-white/40 hover:text-white text-xs pb-2.5">✕</button>
                  </div>
                ))}
                {listRows.length < (q.listMax || 5) && (
                  <button onClick={() => setRows([...listRows, {}])} data-testid={`fm-list-add-${q.id}`}
                    className="text-xs font-bold px-3 py-2 rounded-md" style={{ backgroundColor: "rgba(255,255,255,0.06)", color: C.tealSoft }}>
                    + Add {q.itemLabel}
                  </button>
                )}
              </div>
            )}

            {q.type === "unitecon" && (
              <div className="rounded-xl p-5 border max-w-lg" data-testid="fm-unitecon-card"
                style={{ backgroundColor: C.panel, borderColor: "rgba(123,211,206,0.2)" }}>
                {ue === null ? (
                  <div className="text-sm text-white/45">Calculating from your answers…</div>
                ) : (() => {
                  const f1 = (ue.years || {}).f1 || {};
                  const rows = ue.mode === "unit"
                    ? [["Contribution per unit", f1.contribution_per_unit != null ? `KES ${Number(f1.contribution_per_unit).toLocaleString("en-KE")}` : "—"],
                       ["Contribution margin", f1.contribution_margin != null ? `${Math.round(f1.contribution_margin * 100)}%` : "—"],
                       ["Break-even volume", f1.breakeven_units_month != null ? `${Number(f1.breakeven_units_month).toLocaleString("en-KE")} units / month` : "—"],
                       ["Planned volume (year 1)", f1.avg_units_month != null ? `${Number(f1.avg_units_month).toLocaleString("en-KE")} units / month` : "—"]]
                    : [["CAC — cost to win one customer", f1.cac != null ? `KES ${Number(f1.cac).toLocaleString("en-KE")}` : "—"],
                       ["LTV — lifetime value", f1.ltv != null ? `KES ${Number(f1.ltv).toLocaleString("en-KE")}` : "—"],
                       ["LTV : CAC ratio", f1.ltv_cac != null ? `${f1.ltv_cac}× ${f1.ltv_cac >= 3 ? "✓" : "(funders want 3×+)"}` : "—"],
                       ["Payback period", f1.payback_months != null ? `${f1.payback_months} months` : "—"]];
                  return (
                    <>
                      {rows.map(([label, val]) => (
                        <div key={label} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                          <span className="text-[12px] text-white/55">{label}</span>
                          <span className="text-sm font-bold text-white font-mono-num">{val}</span>
                        </div>
                      ))}
                      {(ue.warnings || []).map((w, i) => (
                        <div key={i} className="text-[11.5px] mt-2" style={{ color: C.orange }}>⚠ {w}</div>
                      ))}
                    </>
                  );
                })()}
              </div>
            )}

            {q.type === "hist3" && (
              <div className="grid grid-cols-3 gap-3">
                {[["h3", "H-3 (3 years ago)"], ["h2", "H-2 (2 years ago)"], ["h1", "H-1 (last year)"]].map(([k, label]) => (
                  <div key={k}>
                    <div className="text-[10px] uppercase tracking-wider text-white/40 font-semibold mb-1">{label}</div>
                    <input type="number" className="w-full px-2.5 py-2 rounded-md text-sm text-white focus:outline-none" style={inputStyle}
                      value={(d || {})[k] ?? ""} data-testid={`fm-hist-${k}`}
                      onChange={(e) => setDraft({ ...(d || {}), [k]: e.target.value })} placeholder="Annual KES" />
                  </div>
                ))}
              </div>
            )}

            {q.type === "rates5" && (
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {["f1", "f2", "f3", "f4", "f5"].map((y, i) => (
                  <div key={y}>
                    <div className="text-[10px] uppercase tracking-wider text-white/40 font-semibold mb-1">{YL[i]} · %/mo</div>
                    <input type="number" className="w-full px-2.5 py-2 rounded-md text-sm text-white focus:outline-none" style={inputStyle}
                      value={Math.round(((d || {})[y] ?? 0) * 100)} data-testid={`fm-rates5-${y}`}
                      onChange={(e) => setDraft({ ...(d || {}), [y]: (Number(e.target.value) || 0) / 100 })} />
                  </div>
                ))}
              </div>
            )}

            {q.type === "scenarios3" && <ScenarioCards d={d} setDraft={setDraft} scenarios={scn} yearLabels={YL} />}

            {q.type === "dscr" && <DscrVerdict dscr={dscrData} yearLabels={YL} onGoSection6={() => jumpTo(6)} />}

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-3 mt-5">
              <button onClick={submitCurrent} data-testid="fm-answer-next"
                className="px-5 py-2.5 rounded-lg font-bold text-sm inline-flex items-center gap-2"
                style={{ backgroundColor: C.teal, color: "#fff" }}>
                {q.type === "info" || q.type === "unitecon" || q.type === "dscr" ? "Got it" : prefilled ? "Confirm" : "Next"} <ArrowRight size={14} />
              </button>
              {q.default !== undefined && q.type !== "info" && (
                <button onClick={useDefault} data-testid="fm-answer-default"
                  className="px-4 py-2.5 rounded-lg text-[12.5px] font-semibold"
                  style={{ backgroundColor: "rgba(245,158,11,0.12)", color: C.orange, border: "1px solid rgba(245,158,11,0.3)" }}>
                  I don't know — use {q.defaultLabel || "the typical default"}
                </button>
              )}
              {q.optional && q.type === "list" && (
                <button onClick={() => commit(q.transform ? q.transform([]) : [])} data-testid="fm-answer-skip"
                  className="text-xs text-white/50 hover:text-white underline underline-offset-4">
                  Skip — none
                </button>
              )}
              {(qIdx > 0 || section > 1) && (
                <button onClick={() => (qIdx > 0 ? jumpTo(section, qIdx - 1) : jumpTo(section - 1))} data-testid="fm-answer-back"
                  className="text-xs text-white/40 hover:text-white inline-flex items-center gap-1">
                  <ArrowLeft size={12} /> Back
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
